// angular libraries
import {AfterViewInit, Component, OnInit, Renderer2, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';

import {PrimeNGConfig, SelectItem} from 'primeng/api';

import {KeycloakEventType, KeycloakService} from "keycloak-angular";
import {KeycloakActionType} from "@app/keycloak/keycloak-action-type";
import {GrowlService, KeycloakSupportEventType, KeycloakSupportService, UserService} from "@app/core";
import {CommonDataService, RouteLink} from '@app/shared';
import {CountdownComponent, CountdownConfig} from "ngx-countdown";

enum MenuMode {
  STATIC,
  OVERLAY,
  SLIM
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})

export class AppComponent implements OnInit, AfterViewInit {

  output: SelectItem[];
  menu: MenuMode = MenuMode.STATIC;
  layout: string = 'default';
  darkMenu: boolean;
  documentClickListener: Function;
  staticMenuInactive: boolean;
  overlayMenuActive: boolean;
  mobileMenuActive: boolean;
  menuClick: boolean;
  menuButtonClick: boolean;
  topbarMenuButtonClick: boolean;
  topbarMenuClick: boolean;
  topbarMenuActive: boolean;

  topbarEssMenuButtonClick: boolean;
  topbarEssMenuClick: boolean;
  topbarEssMenuActive: boolean;
  topbarMssMenuButtonClick: boolean;
  topbarMssMenuClick: boolean;
  topbarMssMenuActive: boolean;

  activeTopbarItem: Element;
  resetSlim: boolean;
  delegates: SelectItem[];

  showConnect: boolean = false;
  showSupport: boolean = false;

  connectNotification: boolean;
  supportRequestCount: boolean;
  defaultView: boolean = true;

  action: string;
  realm: string;

  displaySsoIdleWarningDialog: boolean;
  displaySsoMaxWarningDialog: boolean;

  ssoIdleWarningCountdownConfig: CountdownConfig = {
    demand: true, leftTime: KeycloakSupportService.SsoIdleTimeoutWarning, format: 'mm:ss',
    prettyText: (text) => this.keycloakSupport.prettyText(text)
  };
  ssoMaxWarningCountdownConfig: CountdownConfig = {
    demand: true, leftTime: KeycloakSupportService.SsoMaxTimeoutWarning, format: 'mm:ss',
    prettyText: (text) => this.keycloakSupport.prettyText(text)
  };

  @ViewChild('ssoIdleWarningCountdown', {static: false}) private ssoIdleWarningCountdown: CountdownComponent;
  @ViewChild('ssoMaxWarningCountdown', {static: false}) private ssoMaxWarningCountdown: CountdownComponent;

  constructor(public renderer: Renderer2,
              private router: Router,
              private route: ActivatedRoute,
              private primengConfig: PrimeNGConfig,
              public user: UserService,
              private growl: GrowlService,
              private commonDataService: CommonDataService,
              private keycloakAngular: KeycloakService,
              private keycloakSupport: KeycloakSupportService) {

    const url = new URL(window.location.href);
    this.action = url.searchParams.get("action");
    this.realm = url.searchParams.get("realm");
    this.user.routeToAfterAuthenticated = window.location.pathname;
    console.log("App Component constructor(): url = ", window.location.pathname + window.location.search);

    // handle login/logout
    if (this.action && this.realm) {
      if (this.action == KeycloakActionType.Login) {
        this.keycloakSupport.handleKeycloakLoginRequest(this.realm);
      } else if (this.action == KeycloakActionType.Return) {
        this.keycloakSupport.handleKeycloakReturn(this.realm);
      } else if (this.action == KeycloakActionType.Logout) {
        this.keycloakSupport.handleKeycloakLogout(this.realm);
      }
    } else {
      if (!this.keycloakSupport.validSavedAccessOrRefreshToken()) {
        this.user.resetUser();
      }
      this.keycloakSupport.handleInitialLoginOrReload();
    }

    // handle keycloak events
    keycloakAngular.keycloakEvents$.subscribe({
      next: (e) => {
        const checkAuthPage = window.location.pathname.includes(RouteLink.CheckAuth);
        const emptyPathName = window.location.pathname == "/";

        const indexOfEventType = Object.values(KeycloakEventType).indexOf(e.type);
        console.log("keycloakEvent:", Object.keys(KeycloakEventType)[indexOfEventType], e.args);

        switch (e.type) {
          case KeycloakEventType.OnAuthError:
            break;
          case KeycloakEventType.OnAuthLogout:
            if (!checkAuthPage && !emptyPathName) {
              console.log("force logout or route to the check-auth page...");
              this.user.logout();
            }
            break;
          case KeycloakEventType.OnAuthRefreshError:
            // when there is an error refreshing the token, need to route to the login page
            break;
          case KeycloakEventType.OnAuthRefreshSuccess:
            KeycloakSupportService.tokenRefreshed++;
            KeycloakSupportService.ssoIdleStart = new Date();
            KeycloakSupportService.accessTokenStart = new Date();
            keycloakSupport.resetSsoIdle();
            break;
          case KeycloakEventType.OnAuthSuccess:
            KeycloakSupportService.ssoMaxStart = new Date();
            KeycloakSupportService.accessTokenStart = new Date();
            setTimeout(() => {
                keycloakSupport.keycloakEvents.next({args: true, type: KeycloakSupportEventType.OnSsoMaxWarning});
              },
              (KeycloakSupportService.SsoMaxTimeout - KeycloakSupportService.SsoMaxTimeoutWarning) * 1000);
            this.user.onAuthSuccess();
            break;
          case KeycloakEventType.OnReady: // when keycloakAngular.init() is successfully completed
            break;
          case KeycloakEventType.OnTokenExpired:
            // void keycloakAngular.updateToken();
            break;
        }
      }
    });

    // handle keycloak events not included in the 3rd party library
    // alert user when sso idle timeout or sso max timeout event is near
    keycloakSupport.keycloakEvents.subscribe({
      next: (e) => {
        const checkAuthPage = window.location.pathname.includes(RouteLink.CheckAuth);
        const emptyPathName = window.location.pathname == "/";

        const indexOfEventType = Object.values(KeycloakSupportEventType).indexOf(e.type);
        console.log("keycloakEvent:", Object.keys(KeycloakSupportEventType)[indexOfEventType], e.args);

        switch (e.type) {
          case KeycloakSupportEventType.OnSsoIdle:
            if (e.args) {
              KeycloakSupportService.ssoIdleStart = new Date();
            }
            break;
          case KeycloakSupportEventType.OnSsoIdleWarning:
            if (e.args) {
              this.restartCountdown(this.ssoIdleWarningCountdown);
              KeycloakSupportService.ssoIdleExpireHandler = setTimeout(() => {
                  keycloakSupport.keycloakEvents.next({args: true, type: KeycloakSupportEventType.OnSsoIdleExpire});
                },
                (KeycloakSupportService.SsoMaxTimeoutWarning) * 1000);
              this.displaySsoIdleWarningDialog = true;
            }
            break;
          case KeycloakSupportEventType.OnSsoIdleExpire:
            if (e.args) {
              this.displaySsoIdleWarningDialog = false;
              if (!checkAuthPage && !emptyPathName) {
                console.log("SSO idle reached. force logout or route to the check-auth page...");
                this.user.logout();
              }
            }
            break;
          case KeycloakSupportEventType.OnSsoMaxWarning:
            if (e.args) {
              this.ssoMaxWarningCountdown.begin();
              this.displaySsoMaxWarningDialog = true;
            }
            break;
        }
      }
    });

  }

  ngOnInit() {
    this.primengConfig.ripple = true;

    // disable side menu and change it to overlay menu when login
    const event = new MouseEvent('click', {bubbles: true});
    this.onLoadInitializeView(event);
    this.changeToOverlayMenu();
    this.defaultView = true;
  }

  handleCountDownEvent(event) {

  }

  onClickExtendSession() {
    this.keycloakSupport.updateToken();
    this.displaySsoIdleWarningDialog = false;
  }


  onClickLogout(): void {
    this.user.logout();
  }

  receiveConnectMessageHandler(event: boolean): void {
    this.showConnect = false;
  }

  receiveConnectNotification(event: boolean): void {
    this.connectNotification = event;
  }

  receiveSupportMessageHandler(event: boolean): void {
    this.showSupport = false;
  }

  receiveSupportNotification(event: boolean): void {
    this.supportRequestCount = event;

  }

  ngAfterViewInit(): void {
    this.documentClickListener = this.renderer.listen('body', 'click', (event) => {
      if (!this.menuClick && !this.menuButtonClick) {
        this.mobileMenuActive = false;
        this.overlayMenuActive = false;
        this.resetSlim = true;
      }

      if (!this.topbarMenuClick && !this.topbarMenuButtonClick) {
        this.topbarMenuActive = false;
      }
      if (!this.topbarEssMenuClick && !this.topbarEssMenuButtonClick) {
        this.topbarEssMenuActive = false;
      }
      if (!this.topbarMssMenuClick && !this.topbarMssMenuButtonClick) {
        this.topbarMssMenuActive = false;
      }

      this.menuClick = false;
      this.menuButtonClick = false;
      this.topbarMenuClick = false;
      this.topbarMenuButtonClick = false;
      this.topbarEssMenuButtonClick = false;
      this.topbarMssMenuButtonClick = false;
    });

  }

  onMenuButtonClick(event: Event): void {
    this.menuButtonClick = true;

    if (this.isMobile()) {
      this.mobileMenuActive = !this.mobileMenuActive;
    } else {
      if (this.staticMenu) {
        this.staticMenuInactive = !this.staticMenuInactive;
      } else if (this.overlayMenu) {
        this.overlayMenuActive = !this.overlayMenuActive;
      }
    }
  }

  onLoadInitializeView(event: Event): void {
    this.menuButtonClick = true;

    if (this.isMobile()) {
      this.mobileMenuActive = false;
      this.overlayMenuActive = false;
    } else {
      if (this.staticMenu) {
        this.staticMenuInactive = !this.staticMenuInactive;
      } else if (this.overlayMenu) {
        this.overlayMenuActive = false;
      }
    }

    event.preventDefault();
  }

  onTopbarMenuButtonClick(event: Event): void {
    this.topbarMenuButtonClick = true;
    this.topbarMenuActive = !this.topbarMenuActive;
    if (this.topbarEssMenuActive) {
      this.topbarEssMenuActive = !this.topbarEssMenuActive;
    }
    if (this.topbarMssMenuActive) {
      this.topbarMssMenuActive = !this.topbarMssMenuActive;
    }
    event.preventDefault();
  }

  onTopbarEssMenuButtonClick(event: Event): void {
    this.topbarEssMenuButtonClick = true;
    this.topbarEssMenuActive = !this.topbarEssMenuActive;
    if (this.topbarMenuActive) {
      this.topbarMenuActive = !this.topbarMenuActive;
    }
    if (this.topbarMssMenuActive) {
      this.topbarMssMenuActive = !this.topbarMssMenuActive;
    }
    event.preventDefault();
  }

  onTopbarMssMenuButtonClick(event: Event): void {
    this.topbarMssMenuButtonClick = true;
    this.topbarMssMenuActive = !this.topbarMssMenuActive;
    if (this.topbarMenuActive) {
      this.topbarMenuActive = !this.topbarMenuActive;
    }
    if (this.topbarEssMenuActive) {
      this.topbarEssMenuActive = !this.topbarEssMenuActive;
    }
    event.preventDefault();
  }

  onTopbarItemClick(event: Event, item: Element): void {
    this.topbarMenuButtonClick = true;
    this.topbarMenuActive = false;
    if (this.activeTopbarItem === item) {
      this.activeTopbarItem = null;
    } else {
      this.activeTopbarItem = item;
    }

    event.preventDefault();
  }

  onTopbarMenuClick(event: Event): void {
    this.topbarMenuClick = true;
  }

  onMenuClick(event: Event): void {
    this.menuClick = true;
    this.resetSlim = false;
  }

  get slimMenu(): boolean {
    return this.menu === MenuMode.SLIM;
  }

  get overlayMenu(): boolean {
    return this.menu === MenuMode.OVERLAY;
  }

  get staticMenu(): boolean {
    return this.menu === MenuMode.STATIC;
  }

  changeToSlimMenu(): void {
    this.menu = MenuMode.SLIM;
  }

  changeToOverlayMenu(): void {
    this.menu = MenuMode.OVERLAY;
  }

  changeToStaticMenu(): void {
    this.menu = MenuMode.STATIC;
  }

  isMobile(): boolean {
    return window.innerWidth < 640;
  }

  private restartCountdown(countdown: CountdownComponent) {
    countdown.restart();
    countdown.resume();
  }
}
